import React, { useEffect, useState } from "react";
import { CalendarCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const PHONE_INTL = "36308637476"; // +36 30 863 7476 in WhatsApp format
const WHATSAPP_PREFILL =
  "Sziasztok! Érdeklődnék az Autó-Szerelem szolgáltatásai iránt.";
const WHATSAPP_HREF = `https://wa.me/${PHONE_INTL}?text=${encodeURIComponent(
  WHATSAPP_PREFILL,
)}`;

export default function FloatingCta() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide everything on admin routes
  if (location.pathname.startsWith("/admin")) return null;

  const handleBooking = () => {
    if (location.pathname !== "/") {
      navigate("/#foglalas");
      setTimeout(() => {
        document
          .getElementById("foglalas")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 60);
      return;
    }
    document.getElementById("foglalas")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3"
      data-testid="floating-cta-stack"
    >
      {/* Booking CTA — visible after scroll */}
      <button
        onClick={handleBooking}
        data-testid="floating-cta-booking"
        className={`group transition-all duration-500 ${
          scrolled
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-6 pointer-events-none"
        }`}
        aria-label="Időpontfoglalás"
      >
        <span className="relative inline-flex">
          <span className="absolute inset-0 bg-[#0082D2] opacity-30 animate-ping" />
          <span
            className="relative inline-flex items-center gap-3 bg-[#0082D2] text-white pl-4 pr-5 py-3 sm:py-4 sm:pl-5 sm:pr-6 shadow-[0_0_28px_rgba(0,130,210,0.55)] hover:bg-[#006BB5] transition-all"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            <span className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white/15 border border-white/30">
              <CalendarCheck className="w-5 h-5" strokeWidth={1.8} />
            </span>
            <span className="font-black uppercase tracking-[0.18em] text-sm sm:text-base">
              Időpontfoglalás
            </span>
          </span>
          <span className="absolute -bottom-1 left-0 right-0 m-stripe h-[3px]" />
        </span>
      </button>

      {/* WhatsApp — always visible */}
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-cta-whatsapp"
        aria-label="WhatsApp gyorskapcsolat"
        className="group relative inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1FB955] text-white pl-3 pr-5 py-3 shadow-[0_0_28px_rgba(37,211,102,0.45)] transition-all hover:translate-x-[-2px]"
        style={{ fontFamily: "Barlow Condensed" }}
      >
        <span className="absolute inset-0 bg-[#25D366] opacity-25 animate-ping" />
        <span className="relative w-10 h-10 flex items-center justify-center bg-white/15 border border-white/30 shrink-0">
          <WhatsAppIcon className="w-5 h-5" />
        </span>
        <span className="relative leading-tight">
          <span className="block text-[0.6rem] uppercase tracking-[0.22em] opacity-80">
            Írjon nekünk
          </span>
          <span className="block font-black uppercase tracking-[0.14em] text-sm">
            WhatsApp
          </span>
        </span>
      </a>
    </div>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.05 4.91A10 10 0 0 0 12 2a10 10 0 0 0-8.66 15L2 22l5.18-1.36A10 10 0 0 0 12 22a10 10 0 0 0 7.05-17.09zM12 20.13a8.13 8.13 0 0 1-4.14-1.13l-.3-.18-3.07.81.82-3-.2-.31a8.13 8.13 0 1 1 6.89 3.81zm4.45-6.08c-.24-.12-1.44-.71-1.66-.79s-.39-.12-.55.12-.63.79-.77.95-.28.18-.52.06a6.66 6.66 0 0 1-1.96-1.21 7.4 7.4 0 0 1-1.36-1.69c-.14-.24 0-.37.1-.49s.24-.28.36-.42a1.66 1.66 0 0 0 .24-.4.45.45 0 0 0 0-.42c-.06-.12-.55-1.32-.75-1.81s-.4-.41-.55-.42h-.47a.9.9 0 0 0-.65.3 2.74 2.74 0 0 0-.86 2.04 4.74 4.74 0 0 0 1 2.53 10.86 10.86 0 0 0 4.18 3.7c.58.25 1.04.4 1.39.51a3.36 3.36 0 0 0 1.54.1 2.51 2.51 0 0 0 1.65-1.16 2.04 2.04 0 0 0 .14-1.16c-.06-.1-.22-.16-.46-.28z" />
    </svg>
  );
}
